import { getDoc, setDoc, doc, onSnapshot, Unsubscribe } from "firebase/firestore";
import { database } from "../..";
import type { Agency } from "../../../interfaces/Agency";
import { toast } from "react-toastify";
import { handleAuthError, isAuthError } from "../../../utils/handleAuthError";

// Obtener agencia
const obtenerAgencia = async () => {
  try {
    const docRef = doc(database, "agencia", "default");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data;
    } else {
      console.log("No existe el documento 'default' en agencia");
      return null;
    }
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      return null;
    }
    console.error("Error obteniendo agencia default:", error);
    return null;
  }
};

/**
 * Escucha en tiempo real el documento `agencia/default`.
 *
 * Permite que cambios hechos desde Firebase (por ejemplo `activo`, que suspende
 * o reanuda el servicio) se reflejen en la web sin recargar.
 *
 * Ojo: los errores de `onSnapshot` no llegan como promesa rechazada, así que el
 * handler global de `unhandledrejection` de App.tsx no los ve; por eso aquí se
 * traduce el error de permisos con `handleAuthError`.
 */
const suscribirAgencia = (
  onChange: (agencia: Agency | null) => void,
  onFail?: () => void
): Unsubscribe => {
  const docRef = doc(database, "agencia", "default");

  return onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        onChange(docSnap.data() as Agency);
      } else {
        console.log("No existe el documento 'default' en agencia");
        onChange(null);
      }
    },
    (error) => {
      if (isAuthError(error)) {
        handleAuthError();
        return;
      }
      console.error("Error escuchando agencia default:", error);
      onFail?.();
    }
  );
};

// Actualizar agencia
const actualizarAgencia = async (agencia: Partial<Agency>) => {
  try {
    const docRef = doc(database, "agencia", "default");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      setDoc(docRef, { ...data, ...agencia });
      toast.success("Datos actualizados con éxito");
      return { ...data, ...agencia } as Agency;
    } else {
      console.log("No existe el documento 'default' en agencia");
      return null;
    }
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      return null;
    }
    console.error("Error actualizando agencia: ", error);
    toast.error("Ocurrio un error al actualizar agencia");
    return null;
  }
};

export { obtenerAgencia, suscribirAgencia, actualizarAgencia };
